import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
} from 'react';

interface NavCounts {
  offers: number;
  hosts: number;
  cessions: number;
}

interface LayoutProps {
  counts: NavCounts;
  title: string;
  pathname: string;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  children: ReactNode;
}

interface LayoutContextValue {
  counts: NavCounts;
  navigate: (path: string) => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

interface NavigationLinkProps {
  to: string;
  pathname: string;
  onNavigate: (path: string) => void;
  children: ReactNode;
}

function NavigationLink({
  to,
  pathname,
  onNavigate,
  children,
}: NavigationLinkProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate(to);
  };

  return (
    <a
      href={to}
      className={`nav-link${pathname === to ? ' active' : ''}`}
      onClick={handleClick}
    >
      {children}
    </a>
  );
}

export default function Layout({
  counts,
  title,
  pathname,
  onNavigate,
  onLogout,
  children,
}: LayoutProps) {
  return (
    <LayoutContext.Provider value={{ counts, navigate: onNavigate }}>
      <div className="layout">
        <aside className="sidebar">
          <div className="sidebar-logo">
            <div className="sidebar-logo-mark">S</div>
            <div>
              <div className="sidebar-logo-text">Staynow</div>
            </div>
            <div className="sidebar-logo-badge">ADMIN</div>
          </div>

          <span className="nav-section">Navigation</span>

          <NavigationLink to="/" pathname={pathname} onNavigate={onNavigate}>
            <span className="icon">◈</span>
            Dashboard
          </NavigationLink>

          {counts.offers > 0 && (
            <NavigationLink
              to="/offers"
              pathname={pathname}
              onNavigate={onNavigate}
            >
              <span className="icon">◻</span>
              Offres héritées
              <span className="nav-badge">{counts.offers}</span>
            </NavigationLink>
          )}

          <NavigationLink
            to="/cessions"
            pathname={pathname}
            onNavigate={onNavigate}
          >
            <span className="icon">◈</span>
            Cessions en attente
            <span
              className={`nav-badge${counts.cessions === 0 ? ' zero' : ''}`}
            >
              {counts.cessions}
            </span>
          </NavigationLink>

          <NavigationLink
            to="/hosts"
            pathname={pathname}
            onNavigate={onNavigate}
          >
            <span className="icon">◇</span>
            Hôtes en attente
            <span className={`nav-badge${counts.hosts === 0 ? ' zero' : ''}`}>
              {counts.hosts}
            </span>
          </NavigationLink>

          <NavigationLink
            to="/refunds"
            pathname={pathname}
            onNavigate={onNavigate}
          >
            <span className="icon">↩</span>
            Remboursements
          </NavigationLink>

          {/* The emergency lever. No badge — a badge would imply something is
            waiting, and the day this page matters you will not need reminding. */}
          <NavigationLink
            to="/signal"
            pathname={pathname}
            onNavigate={onNavigate}
          >
            <span className="icon">⚠</span>
            Intégrité du signal
          </NavigationLink>

          {/* No badge: answers arrive by email too, and none of them is work
            waiting for someone the way a pending host is. */}
          <NavigationLink
            to="/surveys"
            pathname={pathname}
            onNavigate={onNavigate}
          >
            <span className="icon">✉</span>
            Sondage bêta
          </NavigationLink>

          {/* Last, and without a badge: it is reference material, not a queue
            of work waiting for someone. */}
          <NavigationLink
            to="/guide"
            pathname={pathname}
            onNavigate={onNavigate}
          >
            <span className="icon">◉</span>
            Guide
          </NavigationLink>

          {/* Sessions expire on their own, but an operator on a shared machine
            needs a way to end one deliberately. */}
          <button type="button" className="nav-logout" onClick={onLogout}>
            <span className="icon">⏻</span>
            Se déconnecter
          </button>
        </aside>

        <div className="main">
          <div className="topbar">
            <span className="topbar-title">{title}</span>
            <span className="topbar-meta">
              <span className="live-dot" style={{ marginRight: 6 }} />
              staynow.live
            </span>
          </div>
          <div className="content">{children}</div>
        </div>
      </div>
    </LayoutContext.Provider>
  );
}

export function useLayoutCounts() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayoutCounts must be used within Layout');
  }
  return context;
}
